import { MAP_STYLE_KEYS, MapStyleKey } from "./mapStyles";
import { MapGroupStyle } from "@/models/MapGroupStyleAddInData";
import { clamp } from "@/utilities/clamp";

/** Names this part of the interface in the user's stored settings. */
export const MAP_SETTINGS_SCOPE = "map:display";

/** Where the map was looking, so it can be put back there. */
export interface MapViewport {
  longitude: number;
  latitude: number;
  zoom: number;
  pitch: number;
  bearing: number;
}

/**
 * Where a map looks before anyone has moved one: the middle of Germany, the country in view.
 *
 * Shared, so the live map and the route map do not open in two different places for a reader who
 * has never dragged either of them.
 */
export const DEFAULT_MAP_VIEWPORT: MapViewport = {
  longitude: 10.0,
  latitude: 51.0,
  zoom: 7,
  pitch: 0,
  bearing: 0,
};

/**
 * Everything about the map a reader can set for themselves.
 *
 * Flat on purpose, apart from the two collections: the settings store copies a value across only
 * when the stored key exists in the defaults and has the same `typeof`, so a shape it can check is
 * a shape that cannot come back wrong after a rename.
 *
 * A type rather than an interface because the settings store takes anything indexable by string,
 * and an interface — which another file could still add to — does not qualify.
 */
export type MapSettings = {
  mapStyle: MapStyleKey;

  /** Geotab's zones, drawn under everything else. */
  showZones: boolean;
  /**
   * VERKEHR.NRW, congestion and roadworks together.
   *
   * One switch rather than one per layer: a reader wants to know what the traffic is doing, not
   * to assemble it from parts.
   */
  showTraffic: boolean;

  showAssetNames: boolean;
  /** The short word for how a vehicle is powered, beside its name on the map. */
  showPowertrainBadges: boolean;
  /** Empty means "whatever the theme says", so the labels follow a theme change. */
  assetNameColor: string;
  assetNameBackgroundColor: string;

  vehicleIconSizePx: number;
  /** Empty means the theme's own colour. */
  vehicleIconColor: string;

  ringFillColor: string;
  /** Per cent. Zero on both opacities is what keeps the ring out of sight until it is wanted. */
  ringFillOpacity: number;
  ringBorderColor: string;
  ringBorderOpacity: number;
  ringBorderWidthPx: number;
  /** Grows or shrinks the ring around the icon. Negative pulls it inside the icon. */
  ringPaddingPx: number;

  clusteringEnabled: boolean;
  clusterRadiusPx: number;
  clusterIconScalePercent: number;

  /** `null` until the reader has moved the map themselves. */
  viewport: MapViewport | null;
};

export const VEHICLE_ICON_SIZE = { min: 10, max: 64 } as const;
export const RING_BORDER_WIDTH = { min: 0, max: 24 } as const;
export const RING_PADDING = { min: -24, max: 48 } as const;
export const OPACITY_PERCENT = { min: 0, max: 100 } as const;
export const CLUSTER_RADIUS = { min: 1, max: 120 } as const;
export const CLUSTER_ICON_SCALE = { min: 60, max: 180 } as const;

/**
 * What the map looks like before anybody changes anything.
 *
 * Chosen by the owner: the OpenStreetMap basemap, small icons in a visible white ring, and
 * clustering only for vehicles practically on the same spot. A reader who has changed a setting
 * keeps their own value.
 */
export const MAP_SETTINGS_DEFAULTS: MapSettings = {
  mapStyle: "osmStandard",

  showZones: false,
  showTraffic: false,

  showAssetNames: false,
  showPowertrainBadges: false,
  assetNameColor: "",
  assetNameBackgroundColor: "",

  vehicleIconSizePx: 16,
  vehicleIconColor: "",

  ringFillColor: "#ffffff",
  ringFillOpacity: 100,
  ringBorderColor: "",
  ringBorderOpacity: 100,
  ringBorderWidthPx: 1,
  ringPaddingPx: 5,

  clusteringEnabled: true,
  clusterRadiusPx: 2,
  clusterIconScalePercent: 60,

  viewport: null,
};

/** Colours handed out to groups as they are picked, so two groups never start out the same. */
export const GROUP_COLOR_PRESETS = [
  "#1976d2",
  "#2e7d32",
  "#ef6c00",
  "#c62828",
  "#6a1b9a",
  "#37474f",
];

/**
 * Pulls every number in the settings back into the range its control allows.
 *
 * Stored settings outlive the code that wrote them: a range that gets narrowed, or a record
 * written by an older version, would otherwise draw a ring wider than the map or a cluster icon
 * the size of a building.
 */
export function withinLimits(settings: MapSettings): MapSettings {
  return {
    ...settings,
    // A basemap that is not on offer here cannot be drawn. That happens to anyone who picked the
    // NRW map where it exists and then opened a deployment without it: the choice survives in
    // their stored settings, and the map would ask a proxy that is not there for every tile.
    mapStyle: MAP_STYLE_KEYS.includes(settings.mapStyle)
      ? settings.mapStyle
      : MAP_SETTINGS_DEFAULTS.mapStyle,
    vehicleIconSizePx: clamp(
      settings.vehicleIconSizePx,
      VEHICLE_ICON_SIZE.min,
      VEHICLE_ICON_SIZE.max,
    ),
    ringFillOpacity: clamp(settings.ringFillOpacity, OPACITY_PERCENT.min, OPACITY_PERCENT.max),
    ringBorderOpacity: clamp(settings.ringBorderOpacity, OPACITY_PERCENT.min, OPACITY_PERCENT.max),
    ringBorderWidthPx: clamp(
      settings.ringBorderWidthPx,
      RING_BORDER_WIDTH.min,
      RING_BORDER_WIDTH.max,
    ),
    ringPaddingPx: clamp(settings.ringPaddingPx, RING_PADDING.min, RING_PADDING.max),
    clusterRadiusPx: clamp(settings.clusterRadiusPx, CLUSTER_RADIUS.min, CLUSTER_RADIUS.max),
    clusterIconScalePercent: clamp(
      settings.clusterIconScalePercent,
      CLUSTER_ICON_SCALE.min,
      CLUSTER_ICON_SCALE.max,
    ),
  };
}

/**
 * A `#rrggbb` colour and a percentage, as the colour deck.gl wants.
 *
 * @param hex - The colour. Anything unreadable falls back to `fallbackHex`.
 * @param opacityPercent - 0 to 100.
 * @param fallbackHex - Used when `hex` is empty or malformed.
 */
export function hexToRgba(
  hex: string,
  opacityPercent: number,
  fallbackHex: string,
): [number, number, number, number] {
  const alpha = Math.round((clamp(opacityPercent, 0, 100) / 100) * 255);
  const parsed = parseHex(hex) ?? parseHex(fallbackHex) ?? [33, 33, 33];
  return [parsed[0], parsed[1], parsed[2], alpha];
}

function parseHex(hex: string): [number, number, number] | null {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) {
    return null;
  }
  const value = Number.parseInt(match[1], 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

/**
 * The styling a vehicle inherits from its group, if any.
 *
 * The first configured group the vehicle belongs to wins — first by the order the groups were
 * added, not by whatever order Geotab happens to return a vehicle's groups in, so a vehicle in
 * two of them does not change colour between refreshes.
 *
 * @param groupIds - The groups the vehicle is in.
 * @param groups - The configured groups, in priority order.
 */
export function groupStyleOf(
  groupIds: readonly string[],
  groups: readonly MapGroupStyle[],
): MapGroupStyle | undefined {
  return groups.find(style => groupIds.includes(style.groupId));
}

/**
 * The colour to draw a vehicle in.
 *
 * @param groupIds - The groups the vehicle is in.
 * @param groups - The configured groups, in priority order.
 * @param vehicleIconColor - The reader's own icon colour, if they set one.
 * @param fallbackColor - Used when nothing else is set.
 */
export function vehicleColorOf(
  groupIds: readonly string[],
  groups: readonly MapGroupStyle[],
  vehicleIconColor: string,
  fallbackColor: string,
): string {
  return groupStyleOf(groupIds, groups)?.color || vehicleIconColor || fallbackColor;
}
